import { Link } from "react-router-dom";
import { Heart, Waves, Sparkles, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const VIDEO_BASSIN = "/media/video-bassin.mp4";
const VIDEO_MER = "/media/video-mer.mp4";

const ROLES = [
  {
    icon: Waves,
    t: "Porter la magie de l'eau",
    d: "Les ambassadeur·rice·s font vivre le Janzu partout où l'eau les porte — en bassin, en mer, à travers le monde.",
  },
  {
    icon: Heart,
    t: "Transmettre le soin",
    d: "Formé·e·s par l'école, ils et elles partagent une pratique reçue, vécue et transmise avec le même soin du détail.",
  },
  {
    icon: Sparkles,
    t: "Représenter l'école",
    d: "Ils incarnent les valeurs OJANZU — douceur, écoute, silence — et parlent de l'école qui les a formé·e·s.",
  },
];

export default function Ambassadeurs() {
  const { tr } = useLang();
  return (
    <main className="min-h-screen bg-abyss pt-[72px] pb-24" data-testid="ambassadeurs-page">
      <div className="relative overflow-hidden pb-16 pt-14">
        <div className="absolute inset-0 caustic-glow" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <SectionLabel>{tr("Communauté OJANZU")}</SectionLabel>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl md:text-5xl font-light leading-tight text-foam">
              {tr("Les Ambassadeurs de l'école")}
            </h1>
            <p className="mt-7 max-w-2xl text-sm md:text-base leading-relaxed text-foam/65">
              {tr("Praticien·ne·s formé·e·s par l'École Française de Janzu, elles et ils font vivre le soin partout où l'eau les porte — en bassin, en mer, à travers le monde. Un cercle de reconnaissance et de transmission, ouvert à celles et ceux qui ont suivi le parcours.")}
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal>
          <div className="overflow-hidden rounded-[2.5rem] bg-[linear-gradient(180deg,#bfe7f4_0%,#e8f7fb_50%,#c8ebf6_100%)] px-6 py-14 shadow-[0_40px_120px_rgba(56,189,248,0.15)] md:px-12 md:py-16">
            <h2 className="text-center font-serif text-3xl md:text-4xl font-light text-abyss">
              {tr("Ils pratiquent, ils transmettent")}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-center text-sm leading-relaxed text-abyss/60">
              {tr("Séances filmées en bassin privé et en mer — l'eau claire, le lâcher-prise, la présence.")}
            </p>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              <figure>
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster="/media/poster-bassin.jpg"
                  src={VIDEO_BASSIN}
                  data-testid="ambassadeurs-video-bassin"
                  className="h-[300px] w-full rounded-3xl object-cover shadow-[0_24px_60px_rgba(3,10,22,0.35)] md:h-[380px]"
                />
                <figcaption className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-abyss/55">
                  {tr("En bassin — eau chaude")}
                </figcaption>
              </figure>
              <figure>
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster="/media/poster-mer.jpg"
                  src={VIDEO_MER}
                  data-testid="ambassadeurs-video-mer"
                  className="h-[300px] w-full rounded-3xl object-cover shadow-[0_24px_60px_rgba(3,10,22,0.35)] md:h-[380px]"
                />
                <figcaption className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-abyss/55">
                  {tr("En mer — lagon turquoise")}
                </figcaption>
              </figure>
            </div>

            <div className="mt-14 grid gap-8 md:grid-cols-3">
              {ROLES.map((r, i) => (
                <Reveal key={r.t} delay={0.08 * i}>
                  <div className="rounded-3xl bg-white/60 p-7 text-center backdrop-blur">
                    <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-abyss text-aqua">
                      <r.icon size={20} />
                    </span>
                    <h3 className="mt-5 font-serif text-xl text-abyss">{tr(r.t)}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-abyss/60">{tr(r.d)}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                data-testid="ambassadeurs-contact-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-abyss px-8 py-4 text-sm font-semibold text-foam transition-all duration-300 hover:bg-aqua hover:text-abyss hover:shadow-[0_0_40px_rgba(56,189,248,0.55)]"
              >
                {tr("Devenir ambassadeur·rice")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/formations"
                className="inline-flex items-center rounded-full border border-abyss/25 px-8 py-4 text-sm font-medium text-abyss transition-colors duration-300 hover:border-abyss hover:bg-abyss hover:text-foam"
              >
                {tr("Rejoindre une formation")}
              </Link>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mx-auto mt-10 max-w-2xl text-center text-sm leading-relaxed text-foam/55">
            {tr("Vous êtes praticien·ne OJANZU et souhaitez rejoindre le cercle des ambassadeur·rice·s ? Écrivez-nous via la page contact — nous serons heureux de vous accueillir.")}
          </p>
        </Reveal>
      </div>
    </main>
  );
}